/**
 * Validate the capability handoff on a `done` event before it reaches the UI.
 *
 * This drives buttons that spend money, so the payload is checked field by
 * field rather than cast. An unrecognised capability or a malformed scope is
 * dropped — the answer still renders, the offer just doesn't.
 *
 * WHAT THIS MUST NOT DO, and did: re-impose the backend's retired gates.
 *
 * The previous version required `capability === 'investigator'` and
 * `member_drug_ids.length > 0`. Those are precisely the two rules deleted from
 * `cepi_capability_handoff.py` when the model took over the decision, and
 * keeping them here silently undid the whole feature in the browser:
 *
 *   - a Cogni or Keyword card failed the capability check
 *   - an Investigator DISCOVERY card (no cohort — which is every page that is
 *     not search results) failed the length check
 *
 * So the backend emitted a correct action, the model reasoned correctly, and
 * the user saw nothing. It survived because the backend probes read
 * `done.actions` directly and the component tests rendered `CapabilityCard`
 * with a hand-built prop — neither path went through this function, which is
 * why it now lives in its own module with its own tests.
 *
 * An empty `member_drug_ids` is VALID and normal: it means discovery, which is
 * Investigator's native mode and the only mode Cogni and Keyword have.
 */
import type { Capability, CapabilityAction } from '../types/qa';

const CAPABILITIES: readonly string[] = ['cogni', 'keyword', 'investigator'];
const COVERAGE: readonly string[] = ['complete', 'discover'];
const OPERATIONS: readonly string[] = ['narrow', 'new_search'];

/** The capability action on a `done` payload, or null. */
export function parseCapabilityAction(doneData: unknown): CapabilityAction | null {
  const actions = (doneData as { actions?: unknown })?.actions;
  if (!Array.isArray(actions)) return null;

  const act = actions.find(
    (a: { type?: string }) => a?.type === 'capability_handoff',
  );
  if (!act) return null;

  const scope = act.scope_draft;
  if (
    !CAPABILITIES.includes(act.capability)
    || typeof act.reason !== 'string' || act.reason.trim() === ''
    || !scope
    || typeof scope.criterion !== 'string' || scope.criterion.trim() === ''
    || !Array.isArray(scope.member_drug_ids)
    || typeof scope.member_count !== 'number'
    || !COVERAGE.includes(scope.coverage_mode)
  ) {
    return null;
  }

  // Only the options we actually implement survive, so a payload naming
  // something else cannot render a button with no handler behind it.
  const options = (Array.isArray(act.options) ? act.options : [])
    .filter((o: string) => o === 'run' || o === 'open');
  if (options.length === 0) return null;

  // Unrecognised or absent -> null, i.e. "no working set", which renders no
  // operation line at all. A backend that predates this field, or one that
  // ever sends a value this build does not know, must degrade to silence
  // rather than to a wrong claim about what the run will do.
  const operation = OPERATIONS.includes(act.operation)
    ? (act.operation as 'narrow' | 'new_search')
    : null;

  return {
    type: 'capability_handoff',
    capability: act.capability as Capability,
    reason: act.reason,
    operation,
    scope_draft: {
      capability: act.capability as Capability,
      criterion: scope.criterion,
      member_drug_ids: scope.member_drug_ids,
      member_count: scope.member_count,
      coverage_mode: scope.coverage_mode,
    },
    options,
    // Strict boolean, not truthiness: a backend that predates this field
    // sends undefined, and `undefined` must render as NO nudge rather than
    // leaking into the JSX as a falsy-but-defined value.
    agents_nudge: act.agents_nudge === true,
  };
}

export default parseCapabilityAction;
